import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAccount, useSignTypedData } from 'wagmi';
import { Loader2, Signature, Wallet, ArrowRight, ShieldAlert } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { useWallet } from '@/context/WalletContext';
import { fetchDemoConfig, submitReceipt, toWalletMessage, newNonce, shortHex } from '@/lib/bindingConsent';
import { ConsentFields, docFingerprint, MAX_WINDOW_HOURS } from '@/components/binding/ConsentFields';
import CheckList from '@/components/binding/CheckList';
import { GateProposedNotice } from '@/components/binding/GateProposedNotice';

const GATE_OPEN = false;

const EMPTY = { document: '', kypaId: '', counterparty: '', hours: '24', notice: '' };

function buildRealMessage(f, principal) {
  const now = Math.floor(Date.now() / 1000);
  return {
    principal, kypaId: f.kypaId.trim(), documentHash: docFingerprint(f.document), actionClass: 3,
    counterparty: f.counterparty.trim(), notBefore: now - 30, expiry: now + Math.round(Number(f.hours) * 3600),
    nonce: newNonce(), notice: f.notice.trim(),
  };
}

export default function BindingGateSign() {
  const { address, isConnected, chainId } = useAccount();
  const { connectWallet, switchToBase } = useWallet();
  const { signTypedDataAsync } = useSignTypedData();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [agent, setAgent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [allow, setAllow] = useState(null);
  const onAgent = useCallback((a) => setAgent(a), []);

  useEffect(() => {
    if (!agent) { setAllow(null); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/binding/principals/${encodeURIComponent(agent.kypa_id)}`, { params: address ? { wallet: address } : {} }).then((r) => setAllow(r.data)).catch(() => setAllow(null));
  }, [agent, address]);
  const onList = Boolean(allow?.set && address && allow.listed);

  useEffect(() => { fetchDemoConfig().then(setCfg).catch(() => setError('Could not load the signing domain. Try again.')); }, []);

  const hours = Number(form.hours);
  const ready = cfg && agent && onList && form.document && form.counterparty.trim() && form.notice.trim() && hours >= 1 && hours <= MAX_WINDOW_HOURS;
  const onBase = cfg && chainId === cfg.domain.chainId;

  const sign = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const message = buildRealMessage(form, address);
      const signature = await signTypedDataAsync({ domain: cfg.domain, types: cfg.types, primaryType: cfg.primary_type, message: toWalletMessage(message) });
      setResult(await submitReceipt(message, signature));
    } catch (e) {
      setError(e?.shortMessage || e?.response?.data?.detail || e?.message || 'Signing failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="binding-sign-page">
      <Helmet><title>Sign a Consent Receipt | Kojiru Binding Action Gate</title></Helmet>
      <Navbar />
      <main className="container mx-auto max-w-3xl px-6 pt-32 pb-20">
        <Link to="/binding-gate" className="text-xs text-red-300 hover:text-red-200" data-testid="sign-back-link">Back to the Binding Action Gate</Link>
        <h1 className="text-4xl sm:text-5xl font-bold mt-4 mb-3" data-testid="sign-page-title">{GATE_OPEN ? 'Sign a consent receipt for your own document' : 'Consent receipt signing is paused'}</h1>
        <p className="text-gray-400 text-base mb-6">{GATE_OPEN ? 'Authorize one registered agent to take one binding action on one exact document, for a window you choose. Signing is free and moves no money.' : 'This page would let a person authorize one registered agent to take one binding action on one exact document. It is not available while the gate is a proposed design.'}</p>
        {!GATE_OPEN ? <GateProposedNotice testid="sign-proposed-notice" /> : (
        <>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-4 mb-8 flex gap-3 text-xs text-amber-200/90 leading-relaxed" data-testid="sign-real-warning">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <span>This is a real receipt, not a demo. Read the full document before you sign. Your wallet address, the agent, and the fingerprint go on the public ledger, and the day&rsquo;s receipts are anchored to Base permanently. The counterparty and your notice stay private. Only wallets on the agent operator&rsquo;s allowlist can sign. Kojiru stores only the fingerprint, never the text. A receipt is an authorization record, not a contract with Kojiru, and not legal advice.</span>
        </div>
        <ConsentFields value={form} onChange={setForm} onAgent={onAgent} />
        {agent && allow && (
          <p className={`mt-3 text-xs ${onList ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="sign-allowlist-status">
            {!allow.set ? 'This agent\'s operator has not set which wallets may authorize it, so the gate refuses every real receipt for now.'
              : !address ? 'Connect your wallet to check whether it is on this agent\'s allowlist.'
              : onList ? 'Your wallet is on this agent\'s allowlist.' : 'Your wallet is not on this agent\'s allowlist. Ask the operator to add it.'}
          </p>
        )}
        <p className="mt-6 text-[11px] text-gray-400" data-testid="sign-real-terms">By signing you agree to the <Link to="/binding-gate/terms" className="text-red-300 underline" data-testid="sign-real-terms-link">Binding Action Gate Terms</Link> and acknowledge the <Link to="/binding-gate/privacy" className="text-red-300 underline" data-testid="sign-real-privacy-link">Privacy Notice</Link>.</p>
        <div className="mt-4 flex items-center gap-3">
          {!isConnected ? (
            <button type="button" onClick={connectWallet} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 px-5 py-2.5 text-sm font-semibold" data-testid="sign-real-connect"><Wallet className="w-4 h-4" /> Connect wallet</button>
          ) : !onBase ? (
            <button type="button" onClick={switchToBase} className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-5 py-2.5 text-sm font-semibold text-amber-300" data-testid="sign-real-switch">Switch to Base to sign</button>
          ) : (
            <button type="button" onClick={sign} disabled={!ready || busy} className="inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-40 px-5 py-2.5 text-sm font-semibold" data-testid="sign-real-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Signature className="w-4 h-4" />} Sign in wallet
            </button>
          )}
          {isConnected && <span className="text-xs font-mono text-gray-400" data-testid="sign-real-address">{shortHex(address)}</span>}
        </div>
        {error && <p className="mt-3 text-xs text-red-400" data-testid="sign-real-error">{error}</p>}
        {result && (
          <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="sign-real-result">
            <div className={`text-xs font-bold uppercase tracking-widest ${result.accepted ? 'text-emerald-300' : 'text-red-300'}`} data-testid="sign-real-status">{result.accepted ? 'Receipt verified and stored' : 'Receipt rejected'}</div>
            {result.receipt && (
              <Link to={`/binding-gate/receipt/${result.receipt.receipt_id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-red-300" data-testid="sign-real-receipt-link">View and share this receipt <ArrowRight className="w-3.5 h-3.5" /></Link>
            )}
            <CheckList checks={result.checks} testid="sign-real-checks" />
            {result.accepted && <p className="mt-3 text-[11px] text-gray-500">Give the agent the exact document text and this receipt fingerprint. Each executed check through the paid gate costs the operator $0.10 USDC.</p>}
          </div>
        )}
        </>
        )}
      </main>
      <Footer />
    </div>
  );
}
